import { Component, OnDestroy, OnInit } from '@angular/core';
import { ColDef } from 'ag-grid-community';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DepartmentService } from '../../../services/department.service';
import { OrganizationService } from '../../../services/organization.service';
import { TenantService } from '../../../services/tenant.service';
import { ActionCellRendererComponent } from '../../../helpers/action-cell-renderer-component/action-cell-renderer.component';
import { ReadOnlyIfNoGroupPermissionDirectiveDirective } from '../../../directives/read-only-if-no-group-permission-directive.directive';

@Component({
  selector: 'app-department-list',
  standalone: true,
  imports: [AgGridAngular, RouterModule, AgGridModule, CommonModule, FormsModule, ReadOnlyIfNoGroupPermissionDirectiveDirective],
  templateUrl: './department-list.component.html',
  styleUrl: './department-list.component.scss'
})
export class DepartmentListComponent implements OnInit, OnDestroy {
  private readonly _destroying$ = new Subject<void>();

  departments: any[] = [];
  isDepartmentEntryComplete: boolean = false;
  sourceDepartmentId: string | null = null;
  selectedDepartments: any[] = [];

  isAllowedTenant: any;

  frameworkComponents = {
    actionCellRenderer: ActionCellRendererComponent
  };

  columnDefs: ColDef[] = [
    { field: 'name', headerName: 'Name', sortable: true, filter: true },
    { field: 'phoneNumber', headerName: 'Phone', sortable: true, filter: true },
    { field: 'serviceDescription', headerName: 'Service Description', sortable: true, filter: true, width: 520, tooltipField: "serviceDescription" },
    {
      field: 'id', headerName: 'Actions', cellRenderer: ActionCellRendererComponent, cellRendererParams:
      {
        context: {
          onDeleteAction: this.onDeleteDepartment.bind(this),
          onEditAction: this.updateDepartment.bind(this)
        }
      }
    }
  ];

  constructor(
    private departmentService: DepartmentService,
    private organizationService: OrganizationService,
    private tenantService: TenantService,
    private router: Router,
    private modalService: NgbModal,
  ) { }

  ngOnInit(): void {

    this.isAllowedTenant = this.tenantService.isAllowedTenant();
    this.loadDepartments();

    //subscribe to changes on the organization id (dropdown / service)
    this.organizationService.organizationId$
      .pipe(takeUntil(this._destroying$))
      .subscribe(() => {
        this.loadDepartments();
      });

    this.organizationService.isDepartmentEntryComplete$.subscribe((value) => {
      this.isDepartmentEntryComplete = value;
    });

  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

loadDepartments() {
    this.departmentService.getDepartments().subscribe({
      next: (data) => {
        this.departments = data ? data.sort((a: any, b: any) => a.name.localeCompare(b.name)) : [];
      },
      error: (error) => {
        console.error('Error loading departments:', error);
        this.departments = [];
      }
    });
  }

  updateDepartment(department: any) {
    this.router.navigate(['/departments/edit', department.id]);
  }

  onDeleteDepartment(department: any) {
    this.departmentService.deleteDepartment(department.id).subscribe(() => {
      this.loadDepartments();
    });
  }

  toggleDepartmentEntryComplete(): void {
    this.organizationService.toggleIsDepartmentEntryComplete().subscribe();
  }

  generateDepartmentsJson() {
    this.departmentService.generateJson(false).subscribe((blob) => {
      // Create a link element, set its download attribute, and trigger a click
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'departments.json';
      link.click();
      window.URL.revokeObjectURL(link.href); // Clean up the URL    
    });
  }

  generateScheduleJson() {
    this.departmentService.generateJson(true).subscribe((blob) => {
      // Create a link element, set its download attribute, and trigger a click
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'departments.json';
      link.click();
      window.URL.revokeObjectURL(link.href); // Clean up the URL    
    });
  }


  // New Method for Importing Departments
  importDepartments(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input?.files?.length) {
      const file = input.files[0];
      const reader = new FileReader();

      reader.onload = () => {
        const jsonContent = reader.result as string;
        this.departmentService.importDepartments(jsonContent).subscribe({
          next: () => {
            this.loadDepartments(); // Refresh department list after successful import
            alert('Departments imported successfully!');
          },
          error: (err) => {
            console.error('Error importing departments:', err);
            alert('An error occurred during import. Please check the console for details.');
          }
        });
      };

      reader.readAsText(file);
    }
  }

  onSelectionChanged(event: any): void {
    this.selectedDepartments = event.api.getSelectedRows();
  }

  openCopyTimeSlotsModal(content: any): void {
    this.sourceDepartmentId = null; // Reset selection
    this.modalService.open(content, { size: 'lg' });  
  }

  
  confirmCopyTimeSlots(modal: any): void {
    if (!this.sourceDepartmentId || this.selectedDepartments.length === 0) return;
  
    this.departmentService.copyTimeSlots(this.sourceDepartmentId, this.selectedDepartments.map(d => d.id))
      .subscribe({
        next: () => {
          alert('Time slots copied successfully!');
          this.loadDepartments(); // Refresh the department list
          modal.close();
        },
        error: (err) => {
          console.error('Error copying time slots:', err);
          alert('An error occurred. Please check the console for details.');
        }
      });
  }


}
